import type { SagaIterator } from 'redux-saga';
import { call, delay, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { crashReporterInterface, httpClientInterface, requireEnvironmentConfig } from 'app-components/utils/core';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { cpMetaForResponseAction } from '../core/actionHelpers';
import type {
  QcsSyncResourcesConfigProps,
  ResourceActionCreator,
  ResourceFailureActionCreator,
} from '../core/resourceStoreTypes';
import { clearOrphanedCreditReport, getCreditReport } from '../credit-report/actions';
import { getCreditReport as selectCreditReport } from '../credit-report/selectors';
import * as actions from './actions';
import { parseCreditProfileRegistration, parseCreditProfileRegistrations } from './creditProfileSchema';
import {
  clearCreditRegistrationFlow,
  getCreditRegistrationFlowId,
  getCreditRegistrationSsnDiagnostics,
  resolveCreditRegistrationSsn,
  sanitizeCreditRegistrationMeta,
} from './registrationFlow';
import { getIsPolling } from './selectors';
import { type CreditProfile, type CreditProfileType, mkCreditProfile } from './types';

const pollingCompletedStatuses = [
  'AUTHENTICATION_COMPLETED',
  'AUTHENTICATION_FAILED',
  'REGISTRATION_COMPLETED',
  'REGISTRATION_FAILED',
];

const mkQcsSyncCreditProfileConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<CreditProfileType>();

const CREDIT_REPORT_RECEIVED_STATUS = 'CREDIT_REPORT_RECEIVED';

const getCreditProfileRegistrationId = (profile: CreditProfileType) =>
  profile.id ?? profile.creditProfileRegistrationId;

// QCS answers the registrations GET with a bare array, not the { resources } page the config type describes.
export const transformCreditProfilesResponse = (response: { data?: unknown } | null | undefined): CreditProfile[] =>
  parseCreditProfileRegistrations(response?.data).map((creditProfile) => mkCreditProfile(creditProfile));

export function* handleGetCreditProfileSuccess(
  action: ReturnType<typeof actions.getCreditProfileSuccess>,
): SagaIterator {
  const profiles = action.payload.resources;
  const creditReportReceivedProfile = profiles.find((p) => p.status === CREDIT_REPORT_RECEIVED_STATUS);

  if (!creditReportReceivedProfile) {
    // Nothing is in a received state, so a cached report is not claimed by anything we know about.
    // Clear before refetching: a profile with no report fails the fetch, which leaves the store as it is.
    const orphanedCreditReport = yield select(selectCreditReport);
    if (orphanedCreditReport) yield put(clearOrphanedCreditReport());

    if (!profiles.length) return;
  }

  // A profile outside CREDIT_REPORT_RECEIVED may still hold a report, so ask and let QCS decide
  // rather than inferring from status.
  const profileToFetch = creditReportReceivedProfile ?? profiles[0];
  const creditProfileRegistrationId = getCreditProfileRegistrationId(profileToFetch);
  if (!creditProfileRegistrationId) {
    return;
  }
  yield put(getCreditReport({ creditProfileRegistrationId }));
}

export const mkResourceConfig = (
  successAction: ResourceActionCreator<CreditProfileType>,
  failureAction: ResourceFailureActionCreator,
  query = '',
  transformResourceWithResponse: QcsSyncResourcesConfigProps<CreditProfileType>['transformResourceWithResponse'] = (
    resource,
    response,
  ) => mkCreditProfile({ ...parseCreditProfileRegistration(response.data), ...resource }),
) =>
  mkQcsSyncCreditProfileConfig({
    resourceName: 'credit-profile-registrations',
    resourceBaseUrl: `/creditscore/credit-profile-registrations${query}`,
    successAction,
    failureAction,
    transformResponseToResources: transformCreditProfilesResponse,
    transformResourceWithResponse,
  });

export function* getCreditProfileActionWatcher() {
  const config = mkResourceConfig(actions.getCreditProfileSuccess, actions.getCreditProfileFailure);

  yield takeEvery(actions.getCreditProfile.type, resourceSagaUtils.qcsSyncGetResources<CreditProfileType>, config);
}

export function* createCreditProfileActionWatcher() {
  yield takeEvery(
    actions.createCreditProfile.type,
    function* handle(action: ReturnType<typeof actions.createCreditProfile>): SagaIterator {
      const ssn = resolveCreditRegistrationSsn(action);
      if (!ssn) {
        const error = new Error(`Missing SSN for ${action.type}`);
        yield call(crashReporterInterface.reportError, error, {
          custom: {
            actionType: action.type,
            ...getCreditRegistrationSsnDiagnostics(action),
          },
        });
        yield put(actions.createCreditProfileFailure(error, cpMetaForResponseAction(action.meta) ?? undefined));
        action.meta?.reject?.(error);
        return;
      }
      // Injected into the request body rather than the payload. The other watchers add a top-level
      // `ssn` that mkCreditProfile drops, but here it belongs under `creditProfile`, which is a real
      // record field - so a payload injection would survive the transformResourceWithResponse spread
      // and land in the store.
      const config = mkResourceConfig(actions.createCreditProfileSuccess, actions.createCreditProfileFailure).set(
        'transformResourceToRequestData',
        (resource) => ({
          ...resource,
          creditProfile: { ...resource.creditProfile, ssn },
        }),
      );
      yield call(resourceSagaUtils.qcsSyncCreateResource<CreditProfileType>, config, {
        ...action,
        meta: sanitizeCreditRegistrationMeta(action.meta),
      });
    },
  );
}

export function* registerCreditProfileActionWatcher() {
  yield takeEvery(
    actions.registerCreditProfile,
    function* handle(action: ReturnType<typeof actions.registerCreditProfile>): SagaIterator {
      const { creditProfileRegistrationId } = action.payload;
      const ssn = resolveCreditRegistrationSsn(action);
      if (!ssn) {
        const error = new Error(`Missing SSN for ${action.type}`);
        yield call(crashReporterInterface.reportError, error, {
          custom: {
            actionType: action.type,
            ...getCreditRegistrationSsnDiagnostics(action),
          },
        });
        yield put(actions.registerCreditProfileFailure(error, cpMetaForResponseAction(action.meta) ?? undefined));
        action.meta?.reject?.(error);
        return;
      }
      const config = mkResourceConfig(
        actions.registerCreditProfileSuccess,
        actions.registerCreditProfileFailure,
        `/${creditProfileRegistrationId}/continue`,
      );
      // The request carries the SSN alongside the resource fields.
      const payload: CreditProfileType & { ssn: string } = { ...action.payload, ssn };
      yield call(resourceSagaUtils.qcsSyncCreateResource<CreditProfileType>, config, {
        ...action,
        payload,
        meta: sanitizeCreditRegistrationMeta(action.meta),
      });
    },
  );
}

type PollActions =
  | ReturnType<typeof actions.pollRegisterCreditProfile>
  | ReturnType<typeof actions.resendVerificationRetrySuccess>;

export function* triggerPollCreditProfile(action: PollActions): SagaIterator {
  let stopPolling = false;
  while (!stopPolling) {
    const result = yield race({
      stop: take(actions.resendVerificationRetrySuccess),
      polling: call(pollRegistrationCheckApi, action),
    });
    if (result.stop || (result.polling && result.polling.stop)) {
      stopPolling = true;
    }
  }
}

function* pollRegistrationCheckApi(action: PollActions): SagaIterator {
  const MAX_ATTEMPTS = 48;
  const DELAY_TIME = 5000; // 5 * 48 = 240sec (4 minutes)

  let idParam;
  if ('creditProfileRegistrationId' in action.payload) {
    idParam = action.payload.creditProfileRegistrationId;
  } else if ('id' in action.payload) {
    idParam = action.payload.id as string;
  }
  const requestUrl = `${requireEnvironmentConfig().services_url}/creditscore/credit-profile-registrations/${idParam}`;
  let attempts = 0;
  let stopPolling = false;
  while (attempts < MAX_ATTEMPTS && !stopPolling) {
    const isPolling = yield select(getIsPolling);
    if (!isPolling) {
      stopPolling = true;
      break;
    }
    try {
      const { data } = yield call(httpClientInterface.send, 'GET', requestUrl);
      if (pollingCompletedStatuses.includes(data.status)) {
        stopPolling = true; // Set flag to stop polling
        const transformedData = [mkCreditProfile(parseCreditProfileRegistration(data))];
        yield put(
          actions.pollRegisterCreditProfileSuccess(
            { resources: transformedData, doMerge: true },
            sanitizeCreditRegistrationMeta(action.meta),
          ),
        );
        break;
      }
      if (data.error) {
        stopPolling = true;
        yield put(actions.registerCreditProfileFailure(data, sanitizeCreditRegistrationMeta(action.meta)));
        break;
      }
    } catch (error: any) {
      stopPolling = true;
      yield put(actions.registerCreditProfileFailure(error, sanitizeCreditRegistrationMeta(action.meta)));
      break;
    }
    attempts++;
    yield delay(DELAY_TIME);
  }
  if (attempts === MAX_ATTEMPTS) {
    stopPolling = true;
    yield put(
      actions.verifyCreditProfileFailure(
        { message: `Credit Registration Polling Stopped After ${attempts} Attempts` },
        sanitizeCreditRegistrationMeta(action.meta),
      ),
    );
  }
  return { stop: stopPolling };
}

export function* pollCreditProfileActionWatcher() {
  yield takeEvery(
    [actions.pollRegisterCreditProfile, actions.resendVerificationRetrySuccess],
    triggerPollCreditProfile,
  );
}

export function* resendVerificationRetryActionWatcher() {
  yield takeEvery(
    actions.resendVerificationRetry,
    function* handle(action: ReturnType<typeof actions.resendVerificationRetry>): SagaIterator {
      const { creditProfileRegistrationId } = action.payload;
      const ssn = resolveCreditRegistrationSsn(action);
      if (!ssn) {
        const error = new Error(`Missing SSN for ${action.type}`);
        yield call(crashReporterInterface.reportError, error, {
          custom: {
            actionType: action.type,
            ...getCreditRegistrationSsnDiagnostics(action),
          },
        });
        yield put(actions.resendVerificationRetryFailure(error, cpMetaForResponseAction(action.meta) ?? undefined));
        action.meta?.reject?.(error);
        return;
      }
      const config = mkResourceConfig(
        actions.resendVerificationRetrySuccess,
        actions.resendVerificationRetryFailure,
        `/${creditProfileRegistrationId}/retry`,
      );
      // The request carries the SSN alongside the resource fields.
      const payload: CreditProfileType & { ssn: string } = { ...action.payload, ssn };
      yield call(resourceSagaUtils.qcsSyncCreateResource<CreditProfileType>, config, {
        ...action,
        payload,
        meta: sanitizeCreditRegistrationMeta(action.meta),
      });
    },
  );
}

export function* deleteCreditProfileActionWatcher() {
  yield takeEvery(actions.deleteCreditProfile, function* handle(action) {
    const { creditProfileRegistrationId } = action.payload;
    const config = mkResourceConfig(
      actions.deleteCreditProfileSuccess,
      actions.deleteCreditProfileFailure,
      `/${creditProfileRegistrationId}`,
      (_resource, response) => ({ ...response.data, id: creditProfileRegistrationId }),
    );
    yield call(resourceSagaUtils.qcsSyncDeleteResource<CreditProfileType>, config, action);
  });
}

export function* listenGetProfileSuccessActionWatcher() {
  yield takeEvery(actions.getCreditProfileSuccess, handleGetCreditProfileSuccess);
}

export function* clearCreditRegistrationFlowActionWatcher() {
  yield takeEvery(
    [actions.createCreditProfileFailure, actions.deleteCreditProfileSuccess],
    function* handle(
      action:
        | ReturnType<typeof actions.createCreditProfileFailure>
        | ReturnType<typeof actions.deleteCreditProfileSuccess>,
    ): SagaIterator {
      yield call(clearCreditRegistrationFlow, getCreditRegistrationFlowId(action));
    },
  );
}

export default [
  getCreditProfileActionWatcher,
  createCreditProfileActionWatcher,
  registerCreditProfileActionWatcher,
  pollCreditProfileActionWatcher,
  resendVerificationRetryActionWatcher,
  deleteCreditProfileActionWatcher,
  listenGetProfileSuccessActionWatcher,
  clearCreditRegistrationFlowActionWatcher,
];
